/**
 * Build-time MCP setup strings (no public config route in Phase A MVP).
 * Override via VITE_* env at build/dev time.
 */

const DEFAULT_PUBLIC = "https://mcpto.chat";

function env(name: keyof ImportMetaEnv, fallback = ""): string {
  const v = import.meta.env[name];
  return typeof v === "string" && v.trim() !== "" ? v.trim() : fallback;
}

/**
 * API base URL.
 * - Production (same origin): empty string → relative `/api/...`
 * - Local without proxy: set VITE_API_ORIGIN (e.g. http://127.0.0.1:8787)
 * - Local with Vite proxy: leave empty so requests stay same-origin (cookies work)
 */
export function apiBaseUrl(): string {
  const origin = env("VITE_API_ORIGIN");
  return origin.replace(/\/$/, "");
}

/**
 * Derive auth.mcpto.chat from the public origin when not overridden.
 * Works for both the apex (mcpto.chat) and a legacy app.* host; returns ""
 * for localhost/IP origins where no Hosted UI subdomain exists.
 */
export function defaultAuthHostedUiBase(publicOrigin: string): string {
  try {
    const u = new URL(publicOrigin);
    const base = u.hostname.startsWith("app.")
      ? u.hostname.slice(4)
      : u.hostname;
    if (!base.includes(".") || /^[\d.]+$/.test(base)) return "";
    u.hostname = `auth.${base}`;
    return u.origin;
  } catch {
    /* ignore */
  }
  return "";
}

export function mcpSetupConfig() {
  const publicOrigin = env("VITE_PUBLIC_ORIGIN", DEFAULT_PUBLIC).replace(
    /\/$/,
    "",
  );
  return {
    publicOrigin,
    // MCP endpoint = the bare origin (/mcp is a legacy alias).
    mcpResource: env("VITE_MCP_RESOURCE", publicOrigin),
    prmUrl: env(
      "VITE_PRM_URL",
      `${publicOrigin}/.well-known/oauth-protected-resource`,
    ),
    mcpClientId: env("VITE_MCP_CLIENT_ID"),
    mcpScope: env("VITE_MCP_SCOPE", "chat-mcp/mcp"),
    cognitoIssuer: env("VITE_COGNITO_ISSUER"),
    /** Cognito Hosted UI (password managers / MCP OAuth login). */
    authHostedUiBase: env(
      "VITE_AUTH_HOSTED_UI_BASE",
      defaultAuthHostedUiBase(publicOrigin),
    ),
  };
}

export const CSRF_COOKIE_NAME = "chat_csrf";
export const CSRF_HEADER_NAME = "x-csrf-token";
